// Wat er openstaat, staat ook in het adres, zodat de terugknop van de browser
// werkt en herladen je op dezelfde plek laat (owner 03-11). Een stap die je met
// terug wilt kunnen ongedaan maken, zet een nieuwe regel in de geschiedenis
// (push); een kleine wijziging binnen dezelfde stap vervangt hem (vervang).
//
// Waarden staan in de zoekparameters (?tab=trends), niet in de hash: het plan
// gebruikt #lijst al, en andere schermen zetten zelf parameters als ?feed=.

import { useEffect, useState } from "react";

export const ADRES_EVENT = "sof:adres";

export function leesParam(naam: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(naam);
  } catch {
    return null;
  }
}

/** Zet (of wist, bij null) één parameter. Laat de rest van het adres staan. */
export function zetParam(naam: string, waarde: string | null, wijze: "push" | "vervang" = "push"): void {
  zetParams({ [naam]: waarde }, wijze, naam);
}

/** Zet of wist meer parameters in één stap (één regel in de geschiedenis).
 *  `extra` komt in de history.state van een nieuwe stap (bv. de diepte van de kaartstapel);
 *  de eigen sporen van een vorige stap (sofKaart) gaan nooit mee naar een andere soort stap. */
export function zetParams(
  delen: Record<string, string | null>,
  wijze: "push" | "vervang" = "push",
  stapNaam?: string,
  extra?: Record<string, unknown>,
): void {
  try {
    const u = new URL(window.location.href);
    let veranderd = false;
    for (const [naam, waarde] of Object.entries(delen)) {
      if ((u.searchParams.get(naam) ?? null) === waarde) continue;
      veranderd = true;
      if (waarde === null) u.searchParams.delete(naam);
      else u.searchParams.set(naam, waarde);
    }
    if (!veranderd) return;
    if (wijze === "push") {
      // De stap onthoudt welke parameter hem maakte: sluitParam weet daardoor of
      // "sluiten" een stap terug in de geschiedenis mag zijn.
      const { sofKaart: _weg, ...rest } = (window.history.state ?? {}) as Record<string, unknown>;
      window.history.pushState({ ...rest, ...(extra ?? {}), sofStap: stapNaam ?? Object.keys(delen)[0] }, "", u);
    } else window.history.replaceState(window.history.state, "", u);
    window.dispatchEvent(new Event(ADRES_EVENT));
  } catch {
    /* geen adresbalk (test) */
  }
}

/** Parameters die bij één tab horen: bij een tabwissel gaan ze mee weg. */
export const TAB_EIGEN_PARAMS = ["skill", "bron", "trend", "kaart", "vlak", "kwaliteit", "lijst", "par", "stap", "k13", "lees", "versie"] as const;

/** Wissel van tab in één stap: de tab erin, de eigen parameters van de vorige tab eruit. */
export function zetTab(waarde: string): void {
  const delen: Record<string, string | null> = { tab: waarde };
  for (const n of TAB_EIGEN_PARAMS) delen[n] = null;
  zetParams(delen, "push", "tab");
}

/** Sluit wat openstond. Maakte jouw eigen klik die stap (push), dan is sluiten
 *  een stap terug (de terugknop en het kruis doen hetzelfde); na herladen of
 *  een gedeelde link is er geen eigen stap en wissen we de parameter ter plekke. */
export function sluitParam(naam: string): void {
  try {
    if (window.history.state?.sofStap === naam) {
      window.history.back();
      return;
    }
  } catch {
    /* geen geschiedenis */
  }
  zetParam(naam, null, "vervang");
}

/** Een waarde die met het adres meeloopt: terug en vooruit in de browser zetten hem ook. */
export function useAdresParam(naam: string): [string | null, (waarde: string | null, wijze?: "push" | "vervang") => void] {
  const [waarde, setWaarde] = useState<string | null>(() => leesParam(naam));
  useEffect(() => {
    const lees = () => setWaarde(leesParam(naam));
    window.addEventListener("popstate", lees);
    window.addEventListener(ADRES_EVENT, lees);
    return () => {
      window.removeEventListener("popstate", lees);
      window.removeEventListener(ADRES_EVENT, lees);
    };
  }, [naam]);
  return [waarde, (w, wijze) => zetParam(naam, w, wijze)];
}
